import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EmptyState from "../components/EmptyState";
import { WORKFLOWS } from "../domain/workflows";
import { api } from "../services/api";
import useAppShellModalIsolation from "../features/transactions/readonly/useAppShellModalIsolation";
import {
  CLOSED_STATUSES,
  LOST_STATUSES,
  categoryFor,
  formatDate,
  money,
  normalize,
  statusTone,
  transactionCoordinatorName,
  transactionDate,
  transactionTypeLabel,
  transactionTypeLetter,
  unique
} from "../features/transactions/readonly/transactionReadOnly";
import "../styles/transactions.css";

const VIEW_OPTIONS = [
  { id: "all", label: "All Transactions", description: "Complete authorized pipeline", category: "" },
  { id: "buyers", label: "Buyer Transactions", description: "Buyer-side transactions only", category: "buyers" },
  { id: "sellers", label: "Seller Transactions", description: "Listings and seller pendings", category: "sellers" },
  { id: "referrals", label: "Referral Transactions", description: "Incoming and outgoing referrals", category: "referrals" }
];

function parseDate(value) {
  if (!value) return null;
  const date = new Date(String(value).length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function SummaryCard({ label, value, detail }) {
  return (
    <article className="transactions-summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function TypeBadge({ transaction, cluster = false }) {
  if (cluster) {
    return (
      <span className="transactions-view-badge-cluster" aria-hidden="true">
        <span className="transactions-type-badge transactions-type-badge--buyer">B</span>
        <span className="transactions-type-badge transactions-type-badge--seller">S</span>
        <span className="transactions-type-badge transactions-type-badge--referral">R</span>
      </span>
    );
  }
  const type = transactionTypeLabel(transaction).toLowerCase();
  return <span className={`transactions-type-badge transactions-type-badge--${type}`} aria-hidden="true">{transactionTypeLetter(transaction)}</span>;
}

function ViewOption({ option, selected, active, onSelect, optionRef, onKeyDown, onFocus }) {
  const transaction = option.category ? { transactionSide: option.category === "sellers" ? "Seller" : option.category === "referrals" ? "Referral" : "Buyer" } : null;
  return (
    <div
      id={`transaction-view-option-${option.id}`}
      ref={optionRef}
      role="option"
      aria-selected={selected}
      tabIndex={active ? 0 : -1}
      className="transactions-view-option"
      onClick={() => onSelect(option)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
    >
      <span className="transactions-view-option-badge">{option.category ? <TypeBadge transaction={transaction} /> : <TypeBadge cluster />}</span>
      <span className="transactions-view-option-copy"><strong>{option.label}</strong><small>{option.description}</small></span>
      <span className="transactions-view-option-check" aria-hidden="true">✓</span>
    </div>
  );
}

function ActiveFilterChip({ filter, index, onRemove }) {
  return (
    <span className="transactions-filter-chip">
      {filter.label}
      <button
        type="button"
        data-filter-chip-index={index}
        aria-label={`Remove ${filter.label}`}
        onClick={() => onRemove(filter.key, index, filter.label)}
      >×</button>
    </span>
  );
}

export default function Transactions({
  transactions = [],
  leadership = false,
  onStart,
  onRefresh,
  onOpenTransaction
}) {
  const [viewId, setViewId] = useState("all");
  const [viewOpen, setViewOpen] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [agent, setAgent] = useState("");
  const [tc, setTc] = useState("");
  const [message, setMessage] = useState("");
  const [approvingId, setApprovingId] = useState("");
  const [announcement, setAnnouncement] = useState("All Transactions selected");
  const [filterAnnouncement, setFilterAnnouncement] = useState("");
  const [mobileSelector, setMobileSelector] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 760px)").matches);
  const triggerRef = useRef(null);
  const filtersButtonRef = useRef(null);
  const selectorLayerRef = useRef(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    const queryList = window.matchMedia("(max-width: 760px)");
    const listener = event => setMobileSelector(event.matches);
    queryList.addEventListener?.("change", listener);
    return () => queryList.removeEventListener?.("change", listener);
  }, []);

  useAppShellModalIsolation(mobileSelector && viewOpen);

  useEffect(() => {
    if (!viewOpen) return undefined;
    const frame = requestAnimationFrame(() => optionRefs.current[activeOptionIndex]?.focus());
    return () => cancelAnimationFrame(frame);
  }, [viewOpen]);

  const categorized = useMemo(
    () => transactions.map(transaction => ({ ...transaction, overviewCategory: categoryFor(transaction) })),
    [transactions]
  );

  const activeView = VIEW_OPTIONS.find(option => option.id === viewId) || VIEW_OPTIONS[0];
  const viewTransactions = useMemo(
    () => activeView.category ? categorized.filter(transaction => transaction.overviewCategory === activeView.category) : categorized,
    [activeView.category, categorized]
  );

  const statuses = useMemo(() => unique(viewTransactions.map(transaction => transaction.status)), [viewTransactions]);
  const agents = useMemo(() => unique(viewTransactions.map(transaction => transaction.agentName)), [viewTransactions]);
  const tcs = useMemo(() => unique(viewTransactions.map(transaction => transactionCoordinatorName(transaction)).filter(value => value !== "Not assigned")), [viewTransactions]);

  const filtered = useMemo(() => {
    const needle = normalize(query);
    const fromDate = parseDate(dateFrom);
    const toDate = parseDate(dateTo);
    if (toDate) toDate.setHours(23, 59, 59, 999);

    return viewTransactions.filter(transaction => {
      const searchable = [
        transaction.propertyAddress,
        transaction.clientName,
        transaction.agentName,
        transactionCoordinatorName(transaction),
        transaction.status,
        transaction.transactionSide,
        transaction.transactionType
      ].map(normalize);
      const date = parseDate(transactionDate(transaction));

      if (needle && !searchable.some(value => value.includes(needle))) return false;
      if (status && transaction.status !== status) return false;
      if (agent && transaction.agentName !== agent) return false;
      if (tc && transactionCoordinatorName(transaction) !== tc) return false;
      if (fromDate && (!date || date < fromDate)) return false;
      if (toDate && (!date || date > toDate)) return false;
      return true;
    });
  }, [agent, dateFrom, dateTo, query, status, tc, viewTransactions]);

  const summary = useMemo(() => filtered.reduce((result, transaction) => {
    const normalizedStatus = normalize(transaction.status);
    result.total += 1;
    if (CLOSED_STATUSES.has(normalizedStatus)) result.closed += 1;
    else if (!LOST_STATUSES.has(normalizedStatus)) {
      result.active += 1;
      result.expectedGci += Number(transaction.adjustedGci || 0);
    }
    return result;
  }, { total: 0, active: 0, closed: 0, expectedGci: 0 }), [filtered]);

  const pendingWorkflow = WORKFLOWS.find(item => item.id === "pending-without-record");
  const advancedFilters = [
    status && { key: "status", label: `Status: ${status}` },
    agent && { key: "agent", label: `Agent: ${agent}` },
    tc && { key: "tc", label: `TC: ${tc}` },
    dateFrom && { key: "dateFrom", label: `Closing from ${formatDate(dateFrom)}` },
    dateTo && { key: "dateTo", label: `Closing through ${formatDate(dateTo)}` }
  ].filter(Boolean);

  function openTransaction(transaction) {
    onOpenTransaction?.(transaction.id);
  }

  async function approve(transaction, event) {
    event.stopPropagation();
    setMessage("");
    setApprovingId(transaction.id);
    try {
      await api.approveTransaction(transaction.id);
      await onRefresh?.();
      setMessage("Transaction submission approved.");
    } catch (error) {
      setMessage(error.message || "The transaction could not be approved.");
    } finally {
      setApprovingId("");
    }
  }

  function openSelector(preferredIndex) {
    const selectedIndex = VIEW_OPTIONS.findIndex(option => option.id === viewId);
    const requestedIndex = Number.isInteger(preferredIndex) ? preferredIndex : selectedIndex;
    const nextIndex = (requestedIndex + VIEW_OPTIONS.length) % VIEW_OPTIONS.length;
    setActiveOptionIndex(nextIndex);
    setViewOpen(true);
  }

  function closeSelector(returnFocus = true) {
    setViewOpen(false);
    if (returnFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function selectView(option) {
    setViewId(option.id);
    setActiveOptionIndex(VIEW_OPTIONS.findIndex(item => item.id === option.id));
    setAnnouncement(`${option.label} selected`);
    setViewOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function moveOption(nextIndex) {
    const index = (nextIndex + VIEW_OPTIONS.length) % VIEW_OPTIONS.length;
    setActiveOptionIndex(index);
    requestAnimationFrame(() => optionRefs.current[index]?.focus());
  }

  function handleOptionKey(event, index, option) {
    if (event.key === "ArrowDown") { event.preventDefault(); moveOption(index + 1); }
    if (event.key === "ArrowUp") { event.preventDefault(); moveOption(index - 1); }
    if (event.key === "Home") { event.preventDefault(); moveOption(0); }
    if (event.key === "End") { event.preventDefault(); moveOption(VIEW_OPTIONS.length - 1); }
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); selectView(option); }
    if (event.key === "Escape") { event.preventDefault(); closeSelector(); }
  }

  function clearAdvancedFilters() {
    setDateFrom("");
    setDateTo("");
    setStatus("");
    setAgent("");
    setTc("");
    setFilterAnnouncement("Advanced filters cleared");
    setFiltersOpen(false);
    requestAnimationFrame(() => filtersButtonRef.current?.focus());
  }

  function removeFilter(key, index, label) {
    if (key === "status") setStatus("");
    if (key === "agent") setAgent("");
    if (key === "tc") setTc("");
    if (key === "dateFrom") setDateFrom("");
    if (key === "dateTo") setDateTo("");
    setFilterAnnouncement(`${label} removed`);
    requestAnimationFrame(() => {
      const remaining = [...document.querySelectorAll(".transactions-filter-chip button")];
      if (remaining[index]) remaining[index].focus();
      else if (remaining[index - 1]) remaining[index - 1].focus();
      else filtersButtonRef.current?.focus();
    });
  }

  function handleMobileLayerKey(event) {
    if (event.key === "Escape") { event.preventDefault(); closeSelector(); return; }
    if (event.key !== "Tab") return;
    const focusable = [...selectorLayerRef.current.querySelectorAll('[data-modal-close], [role="option"][tabindex="0"]')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  const optionList = (
    <div id="transaction-view-listbox" className="transactions-view-listbox" role="listbox" aria-label="Transaction view">
      {VIEW_OPTIONS.map((option, index) => (
        <ViewOption
          key={option.id}
          option={option}
          selected={option.id === viewId}
          active={index === activeOptionIndex}
          onSelect={selectView}
          optionRef={element => { optionRefs.current[index] = element; }}
          onFocus={() => setActiveOptionIndex(index)}
          onKeyDown={event => handleOptionKey(event, index, option)}
        />
      ))}
    </div>
  );

  return (
    <section className="transactions-overview-page">
      <div className="transactions-overview-content">
        <header className="transactions-overview-header">
          <div>
            <span className="transactions-eyebrow">Transaction Pipeline</span>
            <h1>Transactions</h1>
            <p>Review the complete authorized pipeline across Buyer, Seller, and Referral transactions.</p>
          </div>
          {pendingWorkflow && <button type="button" className="transactions-primary-action" onClick={() => onStart?.(pendingWorkflow)}>New Pending Transaction</button>}
        </header>

        {message && <div className="transactions-message" role="status">{message}</div>}

        <section className="transactions-view-row" aria-label="Primary transaction view">
          <div className="transactions-view-selector">
            <span id="transaction-view-label" className="transactions-view-label">Transaction view</span>
            <div className="transactions-view-selector-control">
              <button
                ref={triggerRef}
                type="button"
                className="transactions-view-trigger"
                aria-haspopup="listbox"
                aria-expanded={viewOpen}
                aria-controls="transaction-view-listbox"
                aria-labelledby="transaction-view-label transaction-view-current"
                onClick={() => viewOpen ? closeSelector(false) : openSelector()}
                onKeyDown={event => {
                  if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
                    event.preventDefault();
                    const selectedIndex = VIEW_OPTIONS.findIndex(option => option.id === viewId);
                    openSelector(event.key === "ArrowUp" ? selectedIndex - 1 : selectedIndex);
                  }
                  if (event.key === "Escape" && viewOpen) { event.preventDefault(); closeSelector(); }
                }}
              >
                <span className="transactions-view-trigger-leading">{activeView.category ? <TypeBadge transaction={{ transactionSide: activeView.category === "sellers" ? "Seller" : activeView.category === "referrals" ? "Referral" : "Buyer" }} /> : <TypeBadge cluster />}</span>
                <span className="transactions-view-trigger-copy"><strong id="transaction-view-current">{activeView.label}</strong><small>{activeView.description}</small></span>
                <span className="transactions-view-trigger-chevron" aria-hidden="true">▾</span>
              </button>
              {viewOpen && !mobileSelector && <div className="transactions-view-popover">{optionList}</div>}
            </div>
          </div>
          <p>Showing only transactions available to the current user. Search and filters remain active when the transaction view changes.</p>
        </section>

        <span className="sr-only" aria-live="polite">{announcement}</span>
        <span className="sr-only" aria-live="polite">{filterAnnouncement}</span>

        <section className="transactions-summary-grid" aria-label="Transaction summary">
          <SummaryCard label="Transactions" value={summary.total} detail="Current view" />
          <SummaryCard label="Active" value={summary.active} detail="Open transactions" />
          <SummaryCard label="Closed" value={summary.closed} detail="Completed transactions" />
          <SummaryCard label="Expected GCI" value={money(summary.expectedGci)} detail="Current filtered pipeline" />
        </section>

        <section className="transactions-filter-region" aria-label="Search and transaction filters">
          <div className="transactions-search-row">
            <label className="transactions-search-control" htmlFor="transactions-search"><span>Search</span><span className="transactions-search-input-wrap"><input id="transactions-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search address, client, agent, TC, type, or status" />{query && <button type="button" aria-label="Clear Search" onClick={() => { setQuery(""); requestAnimationFrame(() => document.getElementById("transactions-search")?.focus()); }}>×</button>}</span></label>
            <button ref={filtersButtonRef} type="button" className={`transactions-filters-toggle${advancedFilters.length ? " active" : ""}`} aria-expanded={filtersOpen} aria-controls="transactions-advanced-filters" onClick={() => setFiltersOpen(value => !value)}>Filters{advancedFilters.length ? ` (${advancedFilters.length})` : ""}<span aria-hidden="true">▾</span></button>
          </div>

          {advancedFilters.length > 0 && (
            <div className="transactions-filter-chips" aria-label="Active advanced filters">
              {advancedFilters.map((filter, index) => <ActiveFilterChip key={filter.key} filter={filter} index={index} onRemove={removeFilter} />)}
              <button type="button" className="transactions-clear-all-link" onClick={clearAdvancedFilters}>Clear all</button>
            </div>
          )}

          {filtersOpen && (
            <section id="transactions-advanced-filters" className="transactions-advanced-filters" aria-label="Advanced transaction filters" onKeyDown={event => { if (event.key === "Escape") { setFiltersOpen(false); requestAnimationFrame(() => filtersButtonRef.current?.focus()); } }}>
              <header><div><h2>Filters</h2><p>Refine the transactions shown below.</p></div><button type="button" aria-label="Close Filters" onClick={() => { setFiltersOpen(false); requestAnimationFrame(() => filtersButtonRef.current?.focus()); }}>×</button></header>
              <div className="transactions-advanced-grid">
                <label><span>Status</span><select value={status} onChange={event => setStatus(event.target.value)}><option value="">All statuses</option>{statuses.map(item => <option key={item}>{item}</option>)}</select></label>
                <label><span>Agent</span><select value={agent} onChange={event => setAgent(event.target.value)}><option value="">All agents</option>{agents.map(item => <option key={item}>{item}</option>)}</select></label>
                <label><span>Transaction Coordinator</span><select value={tc} onChange={event => setTc(event.target.value)}><option value="">All TCs</option>{tcs.map(item => <option key={item}>{item}</option>)}</select></label>
                <fieldset><legend>Closing date range</legend><div><label><span>From</span><input type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></label><label><span>Through</span><input type="date" value={dateTo} onChange={event => setDateTo(event.target.value)} /></label></div></fieldset>
              </div>
              <footer><button type="button" className="transactions-secondary-action" onClick={clearAdvancedFilters}>Clear Filters</button><button type="button" className="transactions-primary-action" onClick={() => { setFiltersOpen(false); requestAnimationFrame(() => filtersButtonRef.current?.focus()); }}>Done</button></footer>
            </section>
          )}
        </section>

        <section className="transactions-results-panel">
          <div className="transactions-results-meta">{filtered.length} transaction{filtered.length === 1 ? "" : "s"}</div>
          {filtered.length ? (
            <>
              <div className="transactions-desktop-table">
                <table>
                  <thead><tr><th>Property</th><th>Client</th><th>Agent</th><th>TC</th><th>Status</th><th>Closing</th><th>Expected GCI</th><th>Action</th></tr></thead>
                  <tbody>{filtered.map(transaction => {
                    const type = transactionTypeLabel(transaction).toLowerCase();
                    const canApprove = leadership && normalize(transaction.intakeStatus) === "submitted for review";
                    return (
                      <tr key={transaction.id} className={`transactions-row transactions-row--${type}`} tabIndex="0" onClick={() => openTransaction(transaction)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openTransaction(transaction); } }}>
                        <td><div className="transactions-property-cell"><TypeBadge transaction={transaction} /><div><span>{transactionTypeLabel(transaction)}</span><strong>{transaction.propertyAddress || "Address pending"}</strong></div></div></td>
                        <td>{transaction.clientName || "Client pending"}</td><td>{transaction.agentName || "Not assigned"}</td><td>{transactionCoordinatorName(transaction)}</td>
                        <td><span className={`transactions-status transactions-status--${statusTone(transaction.status)}`}>{transaction.status || "Not set"}</span>{normalize(transaction.intakeStatus) === "submitted for review" && <small>{transaction.intakeStatus}</small>}</td>
                        <td>{formatDate(transaction.closingDate)}</td><td>{money(transaction.adjustedGci)}</td>
                        <td>{canApprove ? <button type="button" className="transactions-row-action" disabled={approvingId === transaction.id} onClick={event => approve(transaction, event)}>{approvingId === transaction.id ? "Approving…" : "Approve"}</button> : <button type="button" className="transactions-row-action transactions-row-action--secondary" onClick={event => { event.stopPropagation(); openTransaction(transaction); }}>View</button>}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>

              <div className="transactions-mobile-cards">{filtered.map(transaction => {
                const type = transactionTypeLabel(transaction).toLowerCase();
                const canApprove = leadership && normalize(transaction.intakeStatus) === "submitted for review";
                return (
                  <article key={transaction.id} className={`transactions-mobile-card transactions-mobile-card--${type}`}>
                    <div className="transactions-mobile-card-type"><TypeBadge transaction={transaction} /><span>{transactionTypeLabel(transaction)}</span></div>
                    <button type="button" className="transactions-card-open" onClick={() => openTransaction(transaction)}><strong>{transaction.propertyAddress || "Address pending"}</strong><small>{transaction.clientName || "Client pending"}</small></button>
                    <div className="transactions-card-grid"><div><span>Status</span><strong>{transaction.status || "Not set"}</strong></div><div><span>Closing</span><strong>{formatDate(transaction.closingDate)}</strong></div><div><span>Agent</span><strong>{transaction.agentName || "Not assigned"}</strong></div><div><span>TC</span><strong>{transactionCoordinatorName(transaction)}</strong></div><div><span>Expected GCI</span><strong>{money(transaction.adjustedGci)}</strong></div><div><span>Approval</span><strong>{transaction.intakeStatus || "Unavailable"}</strong></div></div>
                    {canApprove && <button type="button" className="transactions-card-approve" disabled={approvingId === transaction.id} onClick={event => approve(transaction, event)}>{approvingId === transaction.id ? "Approving…" : "Approve submission"}</button>}
                  </article>
                );
              })}</div>
            </>
          ) : <EmptyState>No production transactions match Search, filters, or the selected transaction view.</EmptyState>}
        </section>
      </div>

      {viewOpen && !mobileSelector && <button type="button" className="transactions-view-backdrop" aria-label="Close Transaction View selector" onClick={() => closeSelector()} />}
      {viewOpen && mobileSelector && typeof document !== "undefined" && createPortal(
        <div ref={selectorLayerRef} className="transactions-view-mobile-layer" role="dialog" aria-modal="true" aria-labelledby="transactions-view-sheet-title" onMouseDown={event => { if (event.target === event.currentTarget) closeSelector(); }} onKeyDown={handleMobileLayerKey}>
          <section className="transactions-view-mobile-sheet">
            <header><div><h2 id="transactions-view-sheet-title">Transaction View</h2><p>Select the authorized pipeline you want to review.</p></div><button type="button" data-modal-close aria-label="Close Transaction View selector" onClick={() => closeSelector()}>×</button></header>
            {optionList}
          </section>
        </div>,
        document.body
      )}
    </section>
  );
}
